import React, { useEffect, useState } from "react";
import {
  Circle,
  Center,
  CircularProgress,
  Flex,
  Tooltip,
  GridItem,
  Icon,
  Text,
  Box,
  Button,
  Input,
  InputLeftElement,
  Card,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FaFileAlt, FaMoneyCheckAlt } from "react-icons/fa";
import { HiIdentification } from "react-icons/hi2";
import { MdNavigateNext } from "react-icons/md";

import { IoIosWarning, IoIosCheckmark } from "react-icons/io";
import { FaChevronRight } from "react-icons/fa";
import { API, graphqlOperation } from "aws-amplify";
import { updateBooking } from "../../graphql/mutations";
import { isHostCoveredDepositStep } from "./stepFlow";

const itemCardStyle = (isComplete) => ({
  cursor: "pointer",
  transition: "all 0.15s ease",
  borderWidth: "1px",
  borderColor: isComplete ? "green.400" : "rgba(129,126,255,0.35)",
  _active: { transform: "scale(0.98)", bg: "whiteAlpha.100" },
});

export const SecuritySteps = ({
  guest,
  room,
  booking,
  handleStatusChange,
  storedStripeId,
  manager,
  accessTimes,
  isPolling = false,
}) => {
  const navigate = useNavigate();

  const getStatusIcon = (isComplete) => {
    if (isComplete) {
      return (
        <Circle size="20px" bg="green.400">
          <Icon as={IoIosCheckmark} boxSize="18px" color="white" />
        </Circle>
      );
    }
    return <Icon as={IoIosWarning} boxSize="18px" color="yellow.400" />;
  };

  const renderSecurityItem = (item) => {
    switch (item.type) {
      case "DOCUMENT":
        return (
          <Card
            key={item.id}
            mt={4}
            p={3}
            w={"full"}
            direction={"row"}
            overflow="hidden"
            variant="outline"
            onClick={() =>
              navigate("/documents", {
                state: { guest, room, booking, storedStripeId, manager, accessTimes },
              })
            }
            {...itemCardStyle(item.isComplete)}
          >
            <Flex justifyContent={"space-between"} w={"100%"} alignItems="center">
              <Flex flexDirection={"column"} flex="1" mr={3}>
                <Flex alignItems="center" mb={1}>
                  <Circle size="30px" bg="#817EFF" mr={2}>
                    <Icon as={FaFileAlt} boxSize={"14px"} />
                  </Circle>
                  <Text fontWeight={800} fontSize="16px">
                    Terms & Agreements
                  </Text>
                </Flex>
                <Text fontWeight={700} fontSize={"11px"} pt={1}>
                  Guest must sign and agree to the uploaded documents after
                  review. Agreement is required to proceed.
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                {getStatusIcon(item.isComplete)}
                {!item.isComplete && (
                  <Icon as={FaChevronRight} boxSize="12px" color="gray.400" />
                )}
              </Flex>
            </Flex>
          </Card>
        );

      case "GUEST_ID":
        // Parse metas to get step notes if available and display is true
        let latestStepNote = null;
        try {
          if (item.metas) {
            const metas = JSON.parse(item.metas);
            if (metas.stepNotes && metas.stepNotes.length > 0) {
              const firstNote = metas.stepNotes[0];
              if (firstNote.display === true) {
                latestStepNote = firstNote.note;
              }
            }
          }
        } catch (e) {
          // Ignore parsing errors
        }

        const isPollingVerification = isPolling && !item.isComplete;

        return (
          <Card
            key={item.id}
            mt={4}
            p={3}
            w={"full"}
            direction={"row"}
            overflow="hidden"
            variant="outline"
            position="relative"
            onClick={() =>
              navigate("/id_verify", {
                state: { guest, booking, room, storedStripeId, manager, accessTimes },
              })
            }
            {...itemCardStyle(item.isComplete)}
            sx={isPollingVerification ? {
              animation: "pulse 3s infinite",
              "@keyframes pulse": {
                "0%, 100%": { 
                  boxShadow: "0 0 0 0 rgba(129, 126, 255, 0.15)"
                },
                "50%": { 
                  boxShadow: "0 0 0 4px rgba(129, 126, 255, 0)"
                }
              }
            } : {}}
          >
            <Flex justifyContent={"space-between"} w={"100%"} alignItems="center">
              <Flex flexDirection={"column"} flex="1" mr={3}>
                <Flex alignItems="center" mb={1}>
                  <Circle size="30px" bg="#817EFF" mr={2}>
                    <Icon as={HiIdentification} boxSize={"14px"} />
                  </Circle>
                  <Text fontWeight={800} fontSize="16px">
                    ID Verification
                    {isPollingVerification && (
                      <Text as="span" fontSize="12px" color="purple.500" ml={2}>
                        (Checking...)
                      </Text>
                    )}
                  </Text>
                </Flex>
                <Text fontWeight={700} fontSize={"11px"} pt={1}>
                  Guest must verify their ID to ensure compliance with security
                  protocols, government photo ID is needed. Verification is
                  required to proceed.
                </Text>
                {latestStepNote && !item.isComplete && (
                  <Box 
                    mt={2} 
                    p={2} 
                    bg="red.50" 
                    borderRadius="6px" 
                    borderLeft="3px solid" 
                    borderColor="red.400"
                  >
                    <Text 
                      fontSize="10px" 
                      fontWeight={600} 
                      color="red.600"
                      lineHeight="1.4"
                    >
                      ⚠️ {latestStepNote}
                    </Text>
                  </Box>
                )}
              </Flex>
              <Flex alignItems="center" gap={2}>
                {isPollingVerification ? (
                  <CircularProgress
                    isIndeterminate
                    color="#817EFF"
                    size="20px"
                    trackColor="transparent"
                  />
                ) : (
                  getStatusIcon(item.isComplete)
                )}
                {!item.isComplete && !isPollingVerification && (
                  <Icon as={FaChevronRight} boxSize="12px" color="gray.400" />
                )}
              </Flex>
            </Flex>
          </Card>
        );

      case "DEPOSIT": {
        return (
          <Card
            key={item.id}
            mt={4}
            p={3}
            w={"full"}
            direction={"row"}
            overflow="hidden"
            variant="outline"
            onClick={() =>
              navigate("/deposit", {
                state: { guest, room, booking, storedStripeId, manager, accessTimes },
              })
            }
            {...itemCardStyle(item.isComplete)}
          >
            <Flex justifyContent={"space-between"} w={"100%"} alignItems="center">
              <Flex flexDirection={"column"} flex="1" mr={3}>
                <Flex alignItems="center" mb={1}>
                  <Circle size="30px" bg="#817EFF" mr={2}>
                    <Icon as={FaMoneyCheckAlt} boxSize={"14px"} />
                  </Circle>
                  <Text fontWeight={800} fontSize="16px">
                    Damage Protection
                  </Text>
                </Flex>
                <Text fontWeight={700} fontSize={"11px"} pt={1}>
                  Guests must secure their reservation through Damage Protection. This ensures protection against any unforeseen damages during the stay.
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                {getStatusIcon(item.isComplete)}
                {!item.isComplete && (
                  <Icon as={FaChevronRight} boxSize="12px" color="gray.400" />
                )}
              </Flex>
            </Flex>
          </Card>
        );
      }
      default:
        return null;
    }
  };

  // Calculate completed and total steps
  // A host-covered (integrated waiver) Damage Protection step is hidden from
  // the guest: telling them the stay is insured invites carelessness. It's
  // created complete, so dropping it never blocks Continue.
  const securityStepItems = booking?.securityItems?.items?.filter(
    (item) => !isHostCoveredDepositStep(item)
  );
  const completedSteps =
    securityStepItems?.filter((item) => item.isComplete).length || 0;
  const totalSteps = securityStepItems?.length || 0;

  // Determine if all steps are complete. `stepsLoaded` distinguishes two
  // states that both look like "zero steps":
  //  - `securityItems.items` missing entirely → the booking came from a
  //    shape that doesn't carry the relation (/bookings/details) or the
  //    GraphQL refetch hasn't landed yet. 0 === 0 used to count as "all
  //    complete" here, enabling Continue with no steps rendered — which
  //    let a guest mark the deposit done and walk into /main-v2 without
  //    completing anything. Blocked.
  //  - `items` is a real (possibly empty) array → the authoritative
  //    getBooking fetch returned, and an empty list means the host
  //    genuinely configured no steps for this booking. Allowed.
  const stepsLoaded = Array.isArray(securityStepItems);
  const allStepsComplete = stepsLoaded && completedSteps === totalSteps;

  const handleSelect = async () => {
    // Belt-and-braces: the button is disabled when steps are incomplete,
    // but never trust the disabled state alone for the gate that writes
    // `deposit: true` to the server.
    if (!allStepsComplete) return;

    try {
      // Update deposit status using handleStatusChange
      handleStatusChange("deposit");

      // The Continue button only enables when every security item is
      // already `isComplete`, so by the time we get here the user has
      // cleared all of them. Mark `securityStepsComplete: true` on the
      // booking handed back so /onboarding's StepChecker doesn't
      // re-fire its `securityStepsComplete === false` gate (which
      // would bounce them right back into SecuritySteps), and pass
      // manager/storedStripeId/accessTimes through so DoneDisplay →
      // /main-v2 still has the data it needs.
      navigate("/onboarding", {
        state: {
          guest,
          room,
          booking: { ...booking, securityStepsComplete: true },
          storedStripeId,
          manager,
          accessTimes,
        },
      });
    } catch (error) {
      console.error("Error during navigation or status update:", error);
    }
  };

  // const handleSelect = async () => {
  //   try {
  //     // Define the explicit status value
  //     const explicitStatus = {
  //       initial: true,
  //       document: true,
  //       card: true,
  //       deposit: true,
  //       done: false,
  //     };

  //     // Only update booking if the type is 'checkIn'
  //     let updatedBooking = booking;

  //     updatedBooking = { ...booking, status: JSON.stringify(explicitStatus) };

  //     await API.graphql(
  //       graphqlOperation(updateBooking, {
  //         input: { id: booking.id, status: updatedBooking.status },
  //       })
  //     );

  //     console.log("Booking updated successfully:", updatedBooking);

  //     // Navigate to /main with the booking info (updated or not based on the type)
  //     // navigate("/main", {
  //     //   state: { room, booking: updatedBooking },
  //     // });
  //   } catch (error) {
  //     console.error("Error updating booking:", error);
  //   }
  // };

  return (
    <Box p={{ base: 4, md: 0 }}>
      <Flex alignItems="center" justifyContent={"flex-end"}>
        <Tooltip
          label={
            allStepsComplete
              ? "All steps are complete."
              : "Complete all steps to continue."
          }
          aria-label="All steps tooltip"
          placement="left"
          hasArrow
          defaultIsOpen
        >
          <Card p={3} direction={"row"} overflow="hidden" variant="outline">
            {getStatusIcon(allStepsComplete)}
            <Text fontWeight={800} fontSize={"12px"} ml={2}>
              {completedSteps}/{totalSteps}
            </Text>
          </Card>
        </Tooltip>
      </Flex>
      <Flex
        // h={"65vh"}
        justifyContent={"flex-start"}
        flexDirection="column"
        mt={"20px"}
      >
        <Center flexDirection="column" alignItems={"start"}>
          <Text fontWeight={800} fontSize={"24px"} pt={"2px"}>
            Let’s Begin!
          </Text>
          <Text mt={1} fontWeight={600} fontSize={"14px"}>
            You’re one step closer.
          </Text>
          {Array.isArray(securityStepItems) &&
            securityStepItems.map(renderSecurityItem)}
        </Center>

        <Button
          // onClick={handleSelect}
          onClick={handleSelect}
          mt={"30px"}
          mb={"40px"}
          color="white"
          bg={"#817EFF"}
          fontSize={"12px"}
          _hover={{ backgroundColor: "#504CFF" }}
          w={"full"}
          p={6}
          borderRadius={"60px"}
          isDisabled={!allStepsComplete}
        >
          Continue <Icon as={MdNavigateNext} fontSize={"22px"} />
        </Button>
      </Flex>
    </Box>
  );
};
